/**
 * A flying route's three input devices as one session: the keyboard
 * (`keyboard.ts`), the mouse (`pointer.ts` for the trigger and the throttle
 * wheel, `free-look-input.ts` for the right button's look-around) and the
 * joystick (`gamepad.ts`).
 *
 * They were three parallel `create…`/`dispose` pairs open-coded in `/fly` and
 * `/duel`, and the joystick would have made it four things to get right twice.
 * More than tidiness, though, it holds one awkwardness that neither route
 * should have to:
 *
 * **the stick has to exist before it is created.** The device belongs in
 * `onMount`, beside the other two, where a `window` is guaranteed — but the
 * PREFERENCES panel's CONTROLS section needs a handle to it from the route's
 * *body*, because the panel is built lazily and may be built before or after
 * the scene. So `stick` is a stable forwarder that finds the device later. It
 * is not a signal and does not need to be: the CONTROLS screen **polls** the
 * device (`screen-runtime.ts`'s `pollLoop`), and a panel opened before `open()`
 * simply reports no stick — which is what a panel opened with nothing plugged
 * in reports too, so there is one empty state rather than two.
 */

import { createFreeLookControls, type FreeLookControls } from "./free-look-input.ts";
import { NO_FREE_LOOK, type FreeLookFrame } from "./free-look.ts";
import { NO_PAD_INPUT, type PadFrame } from "./gamepad-axes.ts";
import { createGamepadControls, type GamepadControls, type GamepadControlsOptions } from "./gamepad.ts";
import { createKeyboardControls, type KeyboardControls } from "./keyboard.ts";
import { createPointerControls, NO_POINTER_INPUT, type PointerControls, type PointerFrame } from "./pointer.ts";
import type { GamepadFeed } from "../shell/controls-surface.ts";

export interface FlightInputSession {
  /** The held keys for this frame. */
  pressedKeys(): ReadonlySet<string>;
  /** The mouse's contribution; reading it consumes the accumulated wheel travel. */
  pointer(): PointerFrame;
  /** The joystick's contribution; reading it polls the device and resolves its button edges. */
  gamepad(): PadFrame;
  /**
   * Where a held right-mouse drag has the pilot looking, and whether he is
   * looking around at all (`free-look.ts`). Reading it advances the eased
   * return by `dtSeconds`, so — like `pointer` and `gamepad` — the scene calls
   * it exactly once per frame.
   *
   * Deliberately *not* part of `pointer()`: a `PointerFrame` is a sim input and
   * goes to `readFlightInputs`, and this is a camera and must never. Keeping
   * them apart is what makes that structural rather than a rule to remember.
   */
  freeLook(dtSeconds: number): FreeLookFrame;
  /** The joystick as the PREFERENCES panel's CONTROLS section reads it — valid before `open()`. */
  readonly stick: GamepadFeed;
  /** Starts listening. Call from `onMount`. */
  open(): void;
  close(): void;
}

const NO_KEYS: ReadonlySet<string> = new Set<string>();

export function createFlightInputSession(options: GamepadControlsOptions): FlightInputSession {
  let keyboard: KeyboardControls | undefined;
  let pointer: PointerControls | undefined;
  let pad: GamepadControls | undefined;
  let freeLook: FreeLookControls | undefined;
  return {
    pressedKeys: () => keyboard?.pressed() ?? NO_KEYS,
    pointer: () => pointer?.frame() ?? NO_POINTER_INPUT,
    gamepad: () => pad?.frame() ?? NO_PAD_INPUT,
    freeLook: (dtSeconds) => freeLook?.frame(dtSeconds) ?? NO_FREE_LOOK,
    stick: {
      snapshot: () => pad?.snapshot(),
      mapping: () => pad?.mapping(),
      setMapping: (next) => pad?.setMapping(next),
    },
    open() {
      keyboard = createKeyboardControls(options.onCommand);
      pointer = createPointerControls();
      pad = createGamepadControls(options);
      freeLook = createFreeLookControls();
    },
    close() {
      keyboard?.dispose();
      pointer?.dispose();
      pad?.dispose();
      freeLook?.dispose();
      keyboard = undefined;
      pointer = undefined;
      pad = undefined;
      freeLook = undefined;
    },
  };
}
